import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAccountDetails } from "../api/apiFunctions";
import { t } from "../i18n";
import { logout } from "../utils/auth";
import { CloseButton } from "react-toastify";

// Utility function to fix S3 URL region mismatch
const fixS3UrlRegion = (url) => {
  if (!url || typeof url !== "string") return url;

  // Fix the region mismatch: replace us-east-1 with eu-north-1
  // This handles both http:// and https:// URLs
  return url.replace(
    /s3\.us-east-1\.amazonaws\.com/g,
    "s3.eu-north-1.amazonaws.com",
  );
};

export default function PatientSidebar({
  selectedTab,
  onSelectTab,
  lang = "en",
  isDrawer = false,
  onCloseDrawer,
  items=[],
  showBackButton = false,
  showBottomContent = false
}) {
  const [userName, setUserName] = useState("Loading...");
  const [userEmail, setUserEmail] = useState("");
  const [profilePictureUrl, setProfilePictureUrl] = useState("");

  const fetchUserData = async () => {
    try {
      const token =
        localStorage.getItem("authToken") ||
        sessionStorage.getItem("authToken");

      if (!token) {
        setUserName("Guest User");
        return;
      }

      const response = await getAccountDetails(token);

      if (response.success && response.data) {
        const data = response.data;
        setUserName(data.fullName || "User");
        setUserEmail(data.email || "");
        // Fix S3 URL region if needed and set profile picture
        const profilePic = data?.profilePicture;
        if (profilePic) {
          const fixedUrl = fixS3UrlRegion(profilePic);
          setProfilePictureUrl(fixedUrl);
        } else {
          setProfilePictureUrl("");
        }
      } else {
        setUserName("User");
      }
    } catch (error) {
      console.error("Error fetching user data for sidebar:", error);
      setUserName("User");
    }
  };

  useEffect(() => {
    // Fetch user data on mount
    fetchUserData();

    // Listen for profile picture updates
    const handleProfilePictureUpdate = (event) => {
      // If the event includes the URL, use it directly (with region fix)
      if (event.detail?.profilePictureUrl) {
        const fixedUrl = fixS3UrlRegion(event.detail.profilePictureUrl);
        setProfilePictureUrl(fixedUrl);
      } else {
        // Otherwise, refetch account details to get the updated picture
        fetchUserData();
      }
    };

    window.addEventListener(
      "profilePictureUpdated",
      handleProfilePictureUpdate,
    );

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener(
        "profilePictureUpdated",
        handleProfilePictureUpdate,
      );
    };
  }, []);

  const navigate = useNavigate();

  // Sidebar classes for drawer/desktop
  const sidebarBase =
    "fixed top-0 left-0 h-full w-64 bg-[#056bfe] flex flex-col text-white shadow-lg z-50 md:static md:shadow-lg md:z-auto md:flex md:h-auto md:w-64";
  const sidebarShape = isDrawer
    ? "rounded-none transition-transform duration-300 transform " +
      (isDrawer ? "translate-x-0" : "-translate-x-full")
    : "rounded-none";
  const sidebarVisibility = isDrawer ? "block md:hidden" : "hidden md:flex";

  // Always show on desktop, only show as drawer on mobile
  const sidebarClass = `${sidebarBase} ${sidebarShape} ${sidebarVisibility}`;

  // Desktop sidebar
  const desktopSidebarClass =
    "hidden md:flex flex-col h-screen w-64 bg-[#056bfe] text-white shadow-lg rounded-none";

  // Handler for Dashboard and Logout
  const handleDashboard = () => {
    if (onCloseDrawer) onCloseDrawer();
    navigate("/patient-dashboard");
  };
  const handleLogout = () => {
    logout();
    if (onCloseDrawer) onCloseDrawer();
    navigate("/login");
  };

  const sidebarContent = (
    <>
    {showBackButton && 
      <button type="button" className="p-5 pb-0 self-start" onClick={() => navigate("/patient-dashboard")}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
    }
      {/* Profile Section */}
      <div className="flex flex-col items-center py-8 border-b border-blue-700">
        <div className="w-20 h-20 rounded-full border-4 border-[#2563eb] bg-white mb-2 overflow-hidden">
          <img
            src={profilePictureUrl}
            alt={userName}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="font-bold text-lg text-white text-center">
          {userName}
        </div>
        {userEmail && (
          <div className="text-sm text-blue-200 text-center mt-1">
            {userEmail}
          </div>
        )}
      </div>
      {/* Navigation */}
      <nav className="flex-1 flex flex-col gap-1 mt-6 px-2 pb-5">
        {items.map((item) => (
          <button
            key={item.label}
            onClick={() => {
              onSelectTab(item.label);
              if (onCloseDrawer) onCloseDrawer();
            }}
            className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors font-medium text-base w-full text-left
              ${
                selectedTab === item.label ? "bg-blue-800" : "hover:bg-blue-700"
              }`}
          >
            <span className="text-xl">{item.icon}</span>
            <span>{t(item.translationKey, lang)}</span>
          </button>
        ))}
      </nav>
      {/* Bottom actions only for drawer (mobile) */}
      {isDrawer && showBottomContent && (
        <div className="mt-auto flex flex-col gap-2 p-4 border-t border-blue-700 md:hidden">
          <button
            className="w-full flex items-center gap-2 px-4 py-2 rounded bg-blue-900 hover:bg-blue-800 font-semibold"
            onClick={handleDashboard}
          >
            <svg
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 8h2v-2H7v2zm0-4h2v-2H7v2zm0-4h2V7H7v2zm4 8h2v-2h-2v2zm0-4h2v-2h-2v2zm0-4h2V7h-2v2zm4 8h2v-2h-2v2zm0-4h2v-2h-2v2zm0-4h2V7h-2v2z" />
            </svg>
            {t("dashboard", lang)}
          </button>
          <button
            className="w-full flex items-center gap-2 px-4 py-2 rounded bg-pink-600 hover:bg-pink-700 font-semibold"
            onClick={handleLogout}
          >
            <svg
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2h4a2 2 0 012 2v1" />
            </svg>
            {t("logout", lang)}
          </button>
        </div>
      )}
    </>
  );

  return (
    <>
      {/* Drawer for mobile */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-[#056bfe] flex flex-col text-white shadow-lg z-50 md:hidden rounded-none transition-transform duration-300 ${isDrawer ? "translate-x-0" : "-translate-x-full"} overflow-y-auto`}
        style={{ transitionProperty: "transform" }}
        tabIndex={-1}
        aria-modal="true"
        role="dialog"
      >
        {sidebarContent}
      </aside>
      {/* Sidebar for desktop */}
      <aside className="hidden md:flex flex-col min-h-screen w-64 bg-[#056bfe] text-white shadow-lg rounded-none">
        {sidebarContent}
      </aside>
    </>
  );
}
